/**
 * School badge rules: which tiers a disciple has reached with the school's neigong, claiming the
 * next one, and choosing which rank of uniform to wear. Pure functions over the progress; the World
 * applies them and announces the results.
 */
import { BADGE_TIERS, type BadgeTier } from './data/badges';
import { SCHOOLS } from './data/schools';
import type { PlayerProgress } from './progress';

export type BadgeStatus = 'claimed' | 'ready' | 'locked';

export interface BadgeView {
  readonly def: BadgeTier;
  readonly status: BadgeStatus;
}

/** Level of the player's school neigong (0 outside a school or before learning it). */
export function schoolNeigongLevel(pr: PlayerProgress): number {
  const sc = pr.school ? SCHOOLS[pr.school] : undefined;
  return sc ? (pr.neigong[sc.neigong] ?? 0) : 0;
}

/** Highest badge tier claimed in the player's current school. */
export const badgeClaimed = (pr: PlayerProgress): number => (pr.school ? (pr.badges[pr.school] ?? 0) : 0);

/** Every tier of the current school, claimed, ready to claim (in order) or still locked. */
export function badgeViews(pr: PlayerProgress): BadgeView[] {
  const level = schoolNeigongLevel(pr);
  const claimed = badgeClaimed(pr);
  return BADGE_TIERS.map((def) => ({
    def,
    status:
      def.tier <= claimed ? 'claimed' : level >= def.level && def.tier === claimed + 1 ? 'ready' : 'locked',
  }));
}

/** Why a tier cannot be claimed now (Thai, for the log), or null when it can. */
export function badgeBlock(pr: PlayerProgress, tier: number): string | null {
  const def = BADGE_TIERS[tier - 1];
  if (!def) return 'ไม่มีป้ายสำนักขั้นนี้';
  if (!pr.school) return 'ต้องเข้าสำนักก่อนจึงจะรับป้ายสำนักได้';
  const claimed = badgeClaimed(pr);
  if (tier <= claimed) return `รับ${def.name}ไปแล้ว`;
  if (tier !== claimed + 1) return `ต้องรับ${BADGE_TIERS[claimed]!.name}ก่อน`;
  const sc = SCHOOLS[pr.school]!;
  if (schoolNeigongLevel(pr) < def.level) return `กำลังภายในสำนักต้องถึงขั้น ${def.level} ก่อน`;
  return sc ? null : 'ไม่พบสำนัก';
}

/** Claims a tier (after `badgeBlock` said it may) and puts on that rank's uniform. */
export function claimBadge(pr: PlayerProgress, tier: number): void {
  if (!pr.school) return;
  pr.badges[pr.school] = tier;
  pr.uniformTier = tier;
}

/** Wears a rank of uniform up to the badge claimed (0 = the plain uniform); false if not allowed. */
export function wearUniformTier(pr: PlayerProgress, tier: number): boolean {
  if (!pr.school || tier < 0 || tier > badgeClaimed(pr) || !Number.isInteger(tier)) return false;
  pr.uniformTier = tier;
  return true;
}

/** The uniform rank shown: what was chosen, never above the current school's badge. */
export const uniformShown = (pr: PlayerProgress): number => Math.min(pr.uniformTier, badgeClaimed(pr));
